// src/b2b/pages/MyBookings.jsx
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { getAuthToken } from '../../auth/token';

function fmtDateFR(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return d;
  }
}

function promoLabel(type, value) {
  if (!type || !value) return '(aucune)';
  if (type === 'amount') return `– ${Number(value).toFixed(2)} €`;
  if (type === 'percent') return `– ${Number(value)} %`;
  return '(aucune)';
}

function computeTotal(b) {
  const base = Number(b.pricePerPerson || 0);
  const final = Number(
    b.finalPricePerPerson ?? base, // côté back on l’a déjà calculé, sinon fallback
  );
  const pax = Number(b.adults || 0) + Number(b.children || 0);
  const total = pax > 0 ? final * pax : final;
  return { base, final, pax, total };
}

export default function MyBookings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);

  async function load(p = 1) {
    setLoading(true);
    setErr(null);
    try {
      const res = await axios.get(
        `/api/bookings/mine?page=${p}&limit=10`,
        { headers: { Authorization: getAuthToken() } },
      );
      setItems(res.data.items || []);
      setPage(res.data.page || 1);
      setPages(res.data.pages || 1);
    } catch (e) {
      setErr(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(1);
  }, []);

  if (loading) return <p className="p-3 text-muted">Chargement…</p>;
  if (err) return <p className="p-3 text-danger">Erreur : {err}</p>;

  return (
    <div className="container my-4" style={{ paddingTop: '200px' }}>
      <h2 className="mb-3">Mes réservations</h2>

      {items.length === 0 ? (
        <div className="alert alert-info">Aucune réservation pour le moment.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>Créée le</th>
                <th>Produit</th>
                <th>Destination</th>
                <th>Départ</th>
                <th>Voyageurs</th>
                <th>Prix base / pers</th>
                <th>Promo</th>
                <th>Prix final / pers</th>
                <th>Total</th>
                <th>Statut</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => {
                const { base, final, pax, total } = computeTotal(b);
                const currency = b.currency || 'EUR';
                return (
                  <tr key={b._id}>
                    <td>{fmtDateFR(b.createdAt)}</td>

                    <td>
                      <div className="fw-semibold">{b.productName || b.productId || '—'}</div>
                    </td>

                    <td>
                      {b?.destination?.city || '—'}
                      {b?.destination?.country ? `, ${b.destination.country}` : ''}
                    </td>

                    <td>
                      <div>{fmtDateFR(b.date || b.startsAt)}</div>
                      <div className="text-muted small">
                        {b.from ? `Depuis : ${b.from}` : ''}
                      </div>
                    </td>

                    <td>
                      {b.adults ?? 0} ad · {b.children ?? 0} enf
                    </td>

                    <td>
                      {base.toFixed(2)} {currency}
                    </td>

                    <td>{promoLabel(b.discountType, b.discountValue)}</td>

                    <td className="fw-semibold">
                      {final.toFixed(2)} {currency}
                    </td>

                    <td className="fw-bold">
                      {total.toFixed(2)} {currency}
                      {pax > 0 && <span className="text-muted small"> ({pax} pax)</span>}
                    </td>

                    <td>
                      <span
                        className={`badge text-bg-${
                          b.status === 'confirmed'
                            ? 'success'
                            : b.status === 'rejected'
                            ? 'danger'
                            : 'warning'
                        }`}
                      >
                        {b.status || 'pending'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          <nav className="d-flex gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={page <= 1}
              onClick={() => load(page - 1)}
            >
              ← Précédent
            </button>
            <span className="align-self-center small">
              Page {page} / {pages}
            </span>
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={page >= pages}
              onClick={() => load(page + 1)}
            >
              Suivant →
            </button>
          </nav>
        </div>
      )}
    </div>
  );
}
